<script>
import ValidationMixin from "@/mixins/validation.mixin";
import { EMAIL_REGEXP, EMAIL_SPLITTER_REGEXP } from "@/constants/regexp.ts";
import { putCursorAtEnd } from "@/directives/cursor.js";
import { useOrder } from "@/composables/index.js";

const { generateId } = useOrder();

export default {
  name: "OrderSendToInput",
  mixins: [ValidationMixin],
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    rules: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      email: null,
      errorMessage: "",
      editableChipIndex: null,
      currentEmails: [],
      emailsField: null,
    };
  },
  computed: {
    model: {
      get() {
        return this.modelValue.map((item) => {
          return {
            ...item,
            id: generateId(),
          };
        });
      },
    },
  },
  watch: {
    model: {
      handler(value) {
        this.currentEmails = value;
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    addEmail() {
      this.errorMessage = "";
      const email = this.email ? this.email.trim() : "";
      this.pushEmail(email);
    },
    pushEmail(email) {
      if (email.length === 0) return;
      if (this.emailValidator(email) === true) {
        const updatedEmails = [...this.currentEmails, { value: email, id: generateId() }];
        this.currentEmails = updatedEmails;
        this.email = null;

        this.$emit("update:modelValue", updatedEmails);
      } else {
        this.errorMessage = "Email not valid";
      }
    },
    validateEmail(email) {
      return EMAIL_REGEXP.test(email);
    },
    onPaste(event) {
      event.preventDefault();
      const data = event.clipboardData.getData("text");
      if (!data) {
        return;
      }

      const emails = data.split(EMAIL_SPLITTER_REGEXP);
      const validEmails = emails.filter((email) => email && this.validateEmail(email));
      validEmails.forEach((email) => {
        this.pushEmail(email);
      });
    },
    startEdit(index) {
      if (index === undefined) {
        return;
      }

      this.editableChipIndex = index;
      this.$nextTick(() => {
        const editable = this.$refs[`chip-edit-${index}`][0];

        if (editable) {
          editable.focus();
          putCursorAtEnd(editable);
        }
      });
    },
    saveEdit(index) {
      this.errorMessage = "";
      const editable = this.$refs[`chip-edit-${index}`]?.[0];

      if (!editable) {
        return;
      }

      const text = editable.innerText.trim();

      if (!text) {
        this.model = this.model.filter((_, idx) => idx !== index);
        return;
      }

      const isValid = this.validateEmail(text);

      if (!isValid) {
        this.errorMessage = "Email not valid";
      }

      const updatedModel = this.model.map((item, idx) => {
        if (idx === index) {
          return {
            ...item,
            value: text,
          };
        }

        return item;
      });

      this.$emit("update:modelValue", updatedModel);

      this.editableChipIndex = null;
    },
    handleEditMenu(isOpen, index) {
      if (isOpen) {
        if (this.editableChipIndex !== null && this.editableChipIndex !== index) {
          this.saveEdit(this.editableChipIndex);
        }

        this.startEdit(index);
      } else {
        if (this.editableChipIndex === index) {
          this.saveEdit(index);
        }
      }
    },
    onCloseChip(id) {
      const updatedModel = this.model.filter((item) => item.id !== id);
      this.$emit("update:modelValue", updatedModel);
    },
  },
};
</script>

<template>
  <v-text-field
    ref="emailsField"
    v-model="email"
    :disabled="disabled"
    :error="Boolean(errorMessage)"
    :error-messages="errorMessage"
    :persistent-placeholder="modelValue.length > 0"
    :rules="[() => !errorMessage, ...rules]"
    autocomplete="off"
    class="required"
    color="primary"
    density="compact"
    hide-details="auto"
    label="Send updates to"
    variant="outlined"
    @blur="addEmail"
    @paste="onPaste"
    @keyup.enter="addEmail"
  >
    <template #prepend-inner>
      <div class="d-flex flex-wrap row-gap-1 py-2">
        <template v-for="(item, idx) in model" :key="item.id">
          <v-menu :close-on-content-click="false" @update:model-value="handleEditMenu($event, idx)">
            <template #activator="{ props }">
              <v-chip
                :class="item.class"
                :closable="true"
                class="mr-1"
                close-icon="mdi-close"
                size="small"
                v-bind="props"
                @click:close.stop="onCloseChip(item.id)"
              >
                {{ item.value }}
              </v-chip>
            </template>

            <v-list>
              <div :ref="`chip-edit-${idx}`" class="px-2">
                <div
                  class="bg-grey-lighten-4 editable-chip"
                  contenteditable="true"
                  @keyup.enter="handleEditMenu(false, idx)"
                >
                  {{ model[editableChipIndex]?.value }}
                </div>
              </div>
            </v-list>
          </v-menu>
        </template>
      </div>
    </template>
  </v-text-field>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.v-chip {
  background-color: $chip-default-color;
}
:deep(.v-field) {
  display: flex;
  flex-wrap: wrap;
}

:deep(.v-field--disabled) {
  opacity: 1;
}

:deep(.v-chip__underlay) {
  background-color: inherit;
}

.editable-chip {
  height: 24px;
  font-size: 14px;
  padding: 2px 8px;
}
</style>
